// @ts-ignore
import { io, Socket } from 'socket.io-client';
import { interactionTracker } from './ai/InteractionTracker';

// Service URLs - All pointing to unified backend
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://techunity-ghbh.onrender.com';
// Force correct path for services on production backend (avoiding /v1 unless explicitly intended)
const normalizeBaseURL = (url: string) => {
  if (!url) return url;
  // Clean trailing slashes
  let cleanUrl = url.replace(/\/+$/, '');

  // Specifically for techunity-ghbh.onrender.com, ensure no /v1
  if (cleanUrl.includes('techunity-ghbh.onrender.com')) {
    // Aggressively strip versioning that might be misconfigured in env vars
    cleanUrl = cleanUrl.replace('/api/v1', '/api');
    cleanUrl = cleanUrl.replace('/v1', '/api');
    // Fix double /api if it happened during replacement
    cleanUrl = cleanUrl.replace('/api/api', '/api');

    // Ensure it ends with /api if it's just the domain or missing /api
    if (cleanUrl.endsWith('.onrender.com')) {
      cleanUrl = `${cleanUrl}/api`;
    } else if (cleanUrl.includes('.onrender.com') && !cleanUrl.includes('/api')) {
      // If it has domain but no /api at all
      cleanUrl = cleanUrl.replace('.onrender.com', '.onrender.com/api');
    }
  }

  // Final safety: remove double slashes (but not after https:)
  cleanUrl = cleanUrl.replace(/([^:]\/)\/+/g, '$1');

  return cleanUrl;
};

// Initial URL construction with aggressive normalization
const API_BASE_URL = normalizeBaseURL(import.meta.env.VITE_API_URL || `${BACKEND_URL}/api`);

// Socket stays on the monolith — Socket.IO instance lives there
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || BACKEND_URL;

// Microservice Primary URLs — configured but potentially ignored for monolith stability
const COLLABORATION_SERVICE_URL = normalizeBaseURL(import.meta.env.VITE_COLLABORATION_SERVICE_URL || `${BACKEND_URL}/api/collaboration`);
const FINANCIAL_SERVICE_URL = normalizeBaseURL(import.meta.env.VITE_FINANCIAL_SERVICE_URL || `${BACKEND_URL}/api/financial`);
const COURSE_SERVICE_URL = normalizeBaseURL(import.meta.env.VITE_COURSE_SERVICE_URL || `${BACKEND_URL}/api`);
const TIMESERIES_SERVICE_URL = normalizeBaseURL(import.meta.env.VITE_TIMESERIES_SERVICE_URL || `${BACKEND_URL}/api`);
const SNN_SERVICE_URL = normalizeBaseURL(import.meta.env.VITE_SNN_SERVICE_URL || `${BACKEND_URL}/api`);

// Unified Monolith Fallback Logic - Re-enabling load-balancing by default
// If you experience "data loss" again, it means your microservices are not using the SAME DB as the monolith!
// In that case, set VITE_FORCE_MONOLITH=true in your environment.
const FORCE_MONOLITH = import.meta.env.VITE_FORCE_MONOLITH === 'true';

const FINAL_BASE_URL = API_BASE_URL;
// Force User & Talent to Monolith 100% of the time to avoid DB fragmented data loss
const FINAL_USER_URL = API_BASE_URL;
const FINAL_TALENT_URL = API_BASE_URL;
const FINAL_COLLAB_URL = FORCE_MONOLITH ? `${API_BASE_URL}/collaboration` : COLLABORATION_SERVICE_URL;
const FINAL_FINANCIAL_URL = FORCE_MONOLITH ? `${API_BASE_URL}/financial` : FINANCIAL_SERVICE_URL;
const FINAL_COURSE_URL = FORCE_MONOLITH ? API_BASE_URL : COURSE_SERVICE_URL;
const FINAL_TIMESERIES_URL = FORCE_MONOLITH ? API_BASE_URL : TIMESERIES_SERVICE_URL;
const FINAL_SNN_URL = FORCE_MONOLITH ? API_BASE_URL : SNN_SERVICE_URL;

// Log final configured URLs for production debugging
console.log('🌐 API Config (Final):', {
  monolith: FINAL_BASE_URL,
  user: FINAL_USER_URL,
  collab: FINAL_COLLAB_URL,
  talent: FINAL_TALENT_URL,
  financial: FINAL_FINANCIAL_URL,
  mode: FORCE_MONOLITH ? 'Monolith-Only (Safe Mode)' : 'Microservices (Load-Balanced)'
});


// API client class
class ApiClient {
  private baseURL: string; // Monolith — the universal fallback
  private userServiceURL: string;
  private collaborationServiceURL: string;
  private financialServiceURL: string;
  private talentServiceURL: string;
  private courseServiceURL: string;
  private timeseriesServiceURL: string;
  private snnServiceURL: string;
  private token: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  // Maps microservice base URL → monolith path prefix (empty string = same path at /api root)
  private microserviceFallbackPaths: Map<string, string>;

  // Cache for instant loading
  private projectsCache: { data: any, timestamp: number } | null = null;
  private roomsCache: { data: any, timestamp: number } | null = null;
  private CACHE_TTL = 60000; // 1 minute cache

  constructor(
    baseURL: string,
    userServiceURL: string,
    collaborationServiceURL: string,
    talentServiceURL: string,
    financialServiceURL: string,
    courseServiceURL: string,
    timeseriesServiceURL: string,
    snnServiceURL: string
  ) {
    this.baseURL = baseURL;
    this.userServiceURL = userServiceURL;
    this.collaborationServiceURL = collaborationServiceURL;
    this.talentServiceURL = talentServiceURL;
    this.financialServiceURL = financialServiceURL;
    this.courseServiceURL = courseServiceURL;
    this.timeseriesServiceURL = timeseriesServiceURL;
    this.snnServiceURL = snnServiceURL;
    this.token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

    // Map each microservice URL to the path prefix it uses on the monolith
    // '' means path is the same (mounts at /api root)
    this.microserviceFallbackPaths = new Map([
      [userServiceURL, ''],
      [collaborationServiceURL, '/collaboration'],
      [talentServiceURL, ''],
      [financialServiceURL, '/financial'],
      [courseServiceURL, ''],
      [timeseriesServiceURL, ''],
      [snnServiceURL, ''],
    ]);
  }

  // HTTP methods for direct API calls
  async get(url: string, options: RequestInit = {}) {
    return this.request(url, { ...options, method: 'GET' });
  }

  async post(url: string, data: any, options: RequestInit = {}) {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(url: string, data: any, options: RequestInit = {}) {
    return this.request(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async patch(url: string, data: any = {}, options: RequestInit = {}) {
    return this.request(url, { ...options, method: 'PATCH', body: JSON.stringify(data) });
  }

  async delete(url: string, options: RequestInit = {}) {
    return this.request(url, { ...options, method: 'DELETE' });
  }

  // ... (rest of class)

  setToken(token: string, remember: boolean = true) {
    this.token = token;
    
    // Safety check: Don't clear tokens if they already exist, only update them
    if (!token) return;

    if (remember) {
      localStorage.setItem('accessToken', token);
      sessionStorage.removeItem('accessToken');
    } else {
      sessionStorage.setItem('accessToken', token);
      localStorage.removeItem('accessToken');
    }
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    window.dispatchEvent(new Event('auth:logout'));
  }

  private async request(endpoint: string, options: RequestInit = {}, originalSpecificBaseURL?: string, _retryCount: number = 0, _forceTarget?: string, _isAuthRetry: boolean = false): Promise<any> {
    const isMicroserviceRoute = originalSpecificBaseURL && this.microserviceFallbackPaths.has(originalSpecificBaseURL) && originalSpecificBaseURL !== this.baseURL;
    
    // Load balancing: 50% chance to hit monolith initially, unless forced or not a microservice route
    let currentTargetBase = originalSpecificBaseURL || this.baseURL;
    let fallbackTargetBase = this.baseURL;
    
    // Safety Force: Authentication always goes to Monolith to avoid "Session/Data Loss"
    const isAuthRoute = endpoint.includes('/auth/');
    if (isAuthRoute) {
      currentTargetBase = this.baseURL;
      fallbackTargetBase = this.baseURL;
    } else if (isMicroserviceRoute && _retryCount === 0 && !_forceTarget && import.meta.env.VITE_DEMO_MODE !== 'true') {
      if (Math.random() < 0.5) {
        currentTargetBase = this.baseURL;
        fallbackTargetBase = originalSpecificBaseURL;
      } else {
        currentTargetBase = originalSpecificBaseURL;
        fallbackTargetBase = this.baseURL;
      }
    } else if (_forceTarget) {
      currentTargetBase = _forceTarget;
      fallbackTargetBase = currentTargetBase === this.baseURL && isMicroserviceRoute ? originalSpecificBaseURL : this.baseURL;
    }

    // Determine the path based on whether we're hitting the monolith
    let requestPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    if (currentTargetBase === this.baseURL && isMicroserviceRoute) {
      const prefix = this.microserviceFallbackPaths.get(originalSpecificBaseURL) ?? '';
      // We assume endpoint passed to specific services doesn't already have the prefix
      if (!requestPath.startsWith(prefix)) {
         requestPath = `${prefix}${requestPath}`;
      }
    } else if (currentTargetBase !== this.baseURL && isMicroserviceRoute) {
      // Ensure the endpoint doesn't have the monolith prefix if we're hitting a microservice directly
      const prefix = this.microserviceFallbackPaths.get(originalSpecificBaseURL) ?? '';
      if (prefix && requestPath.startsWith(prefix)) {
          requestPath = requestPath.replace(prefix, '');
      }
    }

    const url = `${currentTargetBase}${requestPath}`.replace(/([^:]\/)\/+/g, '$1');

    // Diagnostic logging for ALL 404/500 issues in production
    if (import.meta.env.DEV || window.location.hostname.includes('netlify')) {
      console.log(`[ApiClient] ${options.method || 'GET'} -> ${url}`);
    }

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Auto-set Content-Type except for FormData (where browser must set it with boundary)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Add a timeout - reduced for microservices to trigger fallback faster
    const timeout = isMicroserviceRoute ? 15000 : 60000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    let response: Response | undefined;
    let fetchError: any;

    try {
      response = await fetch(url, {
        ...options,
        credentials: 'include', // Ensure cookies are sent even cross-origin
        headers,
        signal: controller.signal,
      });
    } catch (error) {
      fetchError = error;
    } finally {
      clearTimeout(timeoutId);
    }

    const isNetworkError = !!fetchError || !response;
    const isServiceSuspended = response && (response.status >= 502 && response.status <= 504);
    const isMissingRoute = response && response.status === 404;

    // Mutual Fallback - Now triggering on 404 too to handle outdated microservices
    if ((isNetworkError || isServiceSuspended || isMissingRoute) && isMicroserviceRoute && _retryCount === 0) {
      console.warn(`[Fallback] ${isMissingRoute ? 'Route missing' : 'Node unavailable'} at ${currentTargetBase}. Rerouting to ${fallbackTargetBase}...`);
      return this.request(endpoint, options, originalSpecificBaseURL, _retryCount + 1, fallbackTargetBase, _isAuthRetry);
    }

    if (fetchError || !response) {
      interactionTracker.track({
        eventType: 'api_outcome',
        target: endpoint,
        path: window?.location?.pathname || '',
        outcome: 'failure',
        metadata: { method: options.method || 'GET', status: 0, error: 'Network error' }
      });
      throw fetchError || new Error('Network error');
    }

    if (response.status === 401) {
      if (_isAuthRetry) {
        // Break out of potentially infinite loops if the new token is still rejected
        this.clearToken();
        throw new Error('Authentication failed after refresh');
      }

      // Try to refresh token
      const refreshed = await this.refreshToken();
      if (refreshed) {
        console.log(`♻️ Retrying original request to ${endpoint} with auth retry`);
        // Explicitly update the Authorization header with the newly refreshed token
        if (this.token) {
          options.headers = {
            ...options.headers,
            'Authorization': `Bearer ${this.token}`
          };
        }
        // Retry with _isAuthRetry = true
        return this.request(endpoint, options, originalSpecificBaseURL, _retryCount, _forceTarget, true);
      }

      // Redirect to login handled by caller/AuthContext
      console.warn('❌ Token refresh returned false, clearing token');
      this.clearToken();
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));

      // Create a structured error object with details if they exist
      const customError: any = new Error(error.error || 'Request failed');
      customError.status = response.status; // Critical: Add status to the error object

      if (error.details) {
        customError.details = error.details;
      }

      interactionTracker.track({
        eventType: 'api_outcome',
        target: endpoint,
        path: window?.location?.pathname || '',
        outcome: 'failure',
        metadata: { method: options.method || 'GET', status: response.status }
      });

      throw customError;
    }

    if (response.status === 204) {
      return null;
    }

    const data = await response.json();
    
    // Track successful API calls
    interactionTracker.track({
      eventType: 'api_outcome',
      target: endpoint,
      path: window?.location?.pathname || '',
      outcome: 'success',
      metadata: { method: options.method || 'GET', status: response.status }
    });

    return this.mapIds(data);
  }

  // Helper to map _id to id recursively for frontend compatibility
  private mapIds(data: any): any {
    if (!data || typeof data !== 'object') return data;

    if (Array.isArray(data)) {
      return data.map(item => this.mapIds(item));
    }

    // Create shallow copy
    const mapped = { ...data };

    // Map _id to id if id is missing
    if (mapped._id && !mapped.id) {
      mapped.id = mapped._id.toString();
    }

    // Recursively map nested objects
    for (const key in mapped) {
      if (mapped[key] && typeof mapped[key] === 'object' && !(mapped[key] instanceof Date)) {
        mapped[key] = this.mapIds(mapped[key]);
      }
    }

    return mapped;
  }


  // ...

  private async refreshToken(): Promise<boolean> {
    if (this.refreshPromise) {
      console.log('🔄 Token refresh already in progress, joining existing promise...');
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.warn('⚠️ No refresh token available in storage.');
          return false;
        }

        // Add timeout for refresh request
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        try {
          const response = await fetch(`${this.userServiceURL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
            signal: controller.signal,
            credentials: 'include'
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            const data = await response.json();
            const wasInLocalStorage = !!localStorage.getItem('refreshToken');
            const wasInSessionStorage = !!sessionStorage.getItem('refreshToken');
            const mappedData = this.mapIds(data);
            const accessToken = mappedData.accessToken || mappedData.token;

            if (accessToken) {
              this.setToken(accessToken, wasInLocalStorage);
            }

            if (mappedData.refreshToken) {
              if (wasInLocalStorage) {
                localStorage.setItem('refreshToken', mappedData.refreshToken);
              } else if (wasInSessionStorage) {
                sessionStorage.setItem('refreshToken', mappedData.refreshToken);
              }
            }

            console.log('✅ Token refresh successful');
            return true;
          }

          // If client error (4xx), assume token is invalid and we should logout
          if (response.status >= 400 && response.status < 500) {
            const errorData = await response.json().catch(() => ({}));
            console.warn(`❌ Refresh token invalid/expired (Status: ${response.status})`, errorData);
            this.clearToken();
            return false;
          }

          // If server error (5xx), throw but don't clear tokens yet (might be transient)
          if (response.status >= 500) {
            throw new Error(`Server error during refresh: ${response.status}`);
          }

          return false;
        } catch (innerError: any) {
          clearTimeout(timeoutId);
          console.error('❌ Network error during token refresh:', innerError);
          throw innerError;
        }
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  // Auth methods
  async login(email: string, password: string, remember: boolean = true) {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for cold boots

    try {
      console.log('🔐 Attempting login to:', this.userServiceURL);
      console.time('login-request');

      const response = await fetch(`${this.userServiceURL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.timeEnd('login-request');

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Login failed' }));
        console.error('❌ Login failed with status:', response.status, error);
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      console.log('✅ Login successful:', { user: data.user?.email, hasToken: !!(data.accessToken || data.token) });

      // Apply ID mapping
      const mappedData = this.mapIds(data);

      // Handle both token and accessToken for compatibility
      const accessToken = mappedData.accessToken || mappedData.token;
      if (accessToken) {
        this.setToken(accessToken, remember);
      }
      if (mappedData.refreshToken) {
        if (remember) {
          localStorage.setItem('refreshToken', mappedData.refreshToken);
          sessionStorage.removeItem('refreshToken');
        } else {
          sessionStorage.setItem('refreshToken', mappedData.refreshToken);
          localStorage.removeItem('refreshToken');
        }
      }

      return mappedData;
    } catch (error: any) {
      clearTimeout(timeoutId);
      console.timeEnd('login-request');

      if (error.name === 'AbortError') {
        console.error('⏱️ Login timeout after 30 seconds');
        throw new Error('Login request timed out. Please check your connection and try again.');
      }

      console.error('🚨 Login error:', error.message);
      throw error;
    }
  }

  async requestLoginCode(email: string, isRegistering?: boolean, firstName?: string, lastName?: string) {
    // userServiceRequest handles timeout, headers, and error parsing
    return this.userServiceRequest('/auth/request-code', {
      method: 'POST',
      body: JSON.stringify({ email, isRegistering, firstName, lastName }),
    });
  }

  async verifyLoginCode(email: string, code: string, remember: boolean = true) {
    const data = await this.userServiceRequest('/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });

    // Update tokens if present
    const mappedData = this.mapIds(data);
    const accessToken = mappedData.accessToken || mappedData.token;
    if (accessToken) {
      this.setToken(accessToken, remember);
    }
    if (mappedData.refreshToken) {
      if (remember) {
        localStorage.setItem('refreshToken', mappedData.refreshToken);
        sessionStorage.removeItem('refreshToken');
      } else {
        sessionStorage.setItem('refreshToken', mappedData.refreshToken);
        localStorage.removeItem('refreshToken');
      }
    }
    return mappedData;
  }

  async register(userData: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    recaptchaToken?: string;
  }) {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      const response = await fetch(`${this.userServiceURL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Registration failed' }));
        throw new Error(error.error || 'Registration failed');
      }

      const data = await response.json();
      const mappedData = this.mapIds(data);

      // Backend may only return a message on successful registration (verification flow)
      const accessToken = mappedData.accessToken || mappedData.token;
      if (accessToken) {
        this.setToken(accessToken);
      }
      if (mappedData.refreshToken) {
        localStorage.setItem('refreshToken', mappedData.refreshToken);
      }
      return mappedData;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Registration request timed out. Please try again.');
      }
      throw error;
    }
  }

  async logout() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      await fetch(`${this.userServiceURL}/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
    }
    this.clearToken();
  }

  async getCurrentUser() {
    return this.userServiceRequest('/me', {
      method: 'GET'
    });
  }


  // Helper method to make requests to user service
  public async userServiceRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.request(endpoint, options, this.userServiceURL);
  }

  // Helper method to make requests to collaboration service
  public async collaborationServiceRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.request(endpoint, options, this.collaborationServiceURL);
  }

  // Helper method to make requests to course service
  public async courseServiceRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.request(endpoint, options, this.courseServiceURL);
  }

  // Helper method to make requests to talent service
  public async talentServiceRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.request(endpoint, options, this.talentServiceURL);
  }

  // Helper method to make requests to financial service
  public async financialServiceRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.request(endpoint, options, this.financialServiceURL);
  }

  // Helper method to make requests to timeseries service
  public async timeseriesServiceRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.request(endpoint, options, this.timeseriesServiceURL);
  }

  // Helper method to make requests to SNN service
  public async snnServiceRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    return this.request(endpoint, options, this.snnServiceURL);
  }

  async updateUser(updates: any) {
    return this.userServiceRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async uploadAvatar(file: File) {
    const formData = new FormData();
    formData.append('avatar', file);

    const url = `${this.userServiceURL}/users/me/avatar`;
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Avatar upload failed');
    }

    return response.json();
  }

  // Notification methods
  async getUserNotifications() {
    return this.userServiceRequest('/users/notifications');
  }

  async markNotificationAsRead(notificationId: string) {
    return this.userServiceRequest(`/users/notifications/${notificationId}/read`, {
      method: 'PUT'
    });
  }

  async updateUserProfile(profileData: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    bio?: string;
    website?: string;
    location?: string;
    skills?: string[];
    interests?: string[];
    title?: string;
    availability?: string;
    hourlyRate?: number;
    yearsOfExperience?: number;
    portfolioUrl?: string;
    resumeUrl?: string;
    whyJoin?: string;
    goals?: string;
    expertise?: string;
  }) {
    return this.userServiceRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }

  // Room methods
  async getRooms() {
    if (this.roomsCache && (Date.now() - this.roomsCache.timestamp < this.CACHE_TTL)) {
      return this.roomsCache.data;
    }
    const data = await this.collaborationServiceRequest('/rooms');
    this.roomsCache = { data, timestamp: Date.now() };
    return data;
  }

  async getRoom(roomId: string) {
    return this.collaborationServiceRequest(`/rooms/${roomId}`);
  }

  async createRoom(roomData: {
    name: string;
    description?: string;
    type: 'workspace' | '3d_hub' | 'meeting';
    isPrivate?: boolean;
    maxParticipants?: number;
    settings?: any;
    password?: string;
  }) {
    const result = await this.collaborationServiceRequest('/rooms', {
      method: 'POST',
      body: JSON.stringify(roomData),
    });
    this.roomsCache = null; // Invalidate cache
    return result;
  }

  async updateRoom(roomId: string, updates: any) {
    const result = await this.collaborationServiceRequest(`/rooms/${roomId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    this.roomsCache = null; // Invalidate cache
    return result;
  }

  async deleteRoom(roomId: string) {
    const result = await this.collaborationServiceRequest(`/rooms/${roomId}`, {
      method: 'DELETE',
    });
    this.roomsCache = null; // Invalidate cache
    return result;
  }

  async joinRoom(roomId: string) {
    const result = await this.collaborationServiceRequest(`/rooms/${roomId}/join`, {
      method: 'POST',
    });
    this.roomsCache = null; // Invalidate cache
    return result;
  }

  async leaveRoom(roomId: string) {
    const result = await this.collaborationServiceRequest(`/rooms/${roomId}/leave`, {
      method: 'POST',
    });
    this.roomsCache = null; // Invalidate cache
    return result;
  }

  async getRoomMembers(roomId: string) {
    return this.collaborationServiceRequest(`/rooms/${roomId}/members`);
  }

  // Project methods
  async getClientDashboardMetrics() {
    return this.collaborationServiceRequest('/dashboard/client-metrics');
  }

  async getProjects(roomId?: string) {
    // Only cache request for all projects (dashboard view)
    if (!roomId && this.projectsCache && (Date.now() - this.projectsCache.timestamp < this.CACHE_TTL)) {
      return this.projectsCache.data;
    }

    const query = roomId ? `?roomId=${roomId}` : '';
    const data = await this.collaborationServiceRequest(`/projects${query}`);

    if (!roomId) {
      this.projectsCache = { data, timestamp: Date.now() };
    }
    return data;
  }

  async createProject(projectData: any) {
    const result = await this.collaborationServiceRequest('/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
    this.projectsCache = null; // Invalidate cache
    return result;
  }

  async getProjectById(projectId: string) {
    return this.collaborationServiceRequest(`/projects/${projectId}`);
  }

  async updateProject(projectId: string, updates: any) {
    const result = await this.collaborationServiceRequest(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    this.projectsCache = null; // Invalidate cache
    return result;
  }

  async deleteProject(projectId: string) {
    const result = await this.collaborationServiceRequest(`/projects/${projectId}`, {
      method: 'DELETE',
    });
    this.projectsCache = null; // Invalidate cache
    return result;
  }

  // Document methods
  async getDocuments(roomId?: string, projectId?: string) {
    const params = new URLSearchParams();
    if (roomId) params.append('roomId', roomId);
    if (projectId) params.append('projectId', projectId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.collaborationServiceRequest(`/documents${query}`);
  }

  async createDocument(documentData: any) {
    return this.collaborationServiceRequest('/documents', {
      method: 'POST',
      body: JSON.stringify(documentData),
    });
  }

  async updateDocument(documentId: string, updates: any) {
    return this.collaborationServiceRequest(`/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteDocument(documentId: string) {
    return this.collaborationServiceRequest(`/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  // File methods
  async uploadFile(file: File, roomId?: string, onProgress?: (progressEvent: ProgressEvent) => void): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (roomId) formData.append('roomId', roomId);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.collaborationServiceURL}/files/upload`, true);

      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }
      xhr.withCredentials = true;

      // Track upload progress
      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = onProgress;
      }

      const timeoutId = setTimeout(() => {
        xhr.abort();
        reject(new Error('File upload timed out'));
      }, 60000); // Increased timeout to 60s for huge files

      xhr.onload = () => {
        clearTimeout(timeoutId);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            resolve(xhr.responseText);
          }
        } else {
          reject(new Error(xhr.responseText || 'File upload failed'));
        }
      };

      xhr.onerror = () => {
        clearTimeout(timeoutId);
        reject(new Error('File upload encountered a network error'));
      };

      xhr.send(formData);
    });
  }

  async getFiles(roomId?: string) {
    const query = roomId ? `?roomId=${roomId}` : '';
    return this.collaborationServiceRequest(`/files${query}`);
  }

  async deleteFile(fileId: string) {
    return this.collaborationServiceRequest(`/files/${fileId}`, {
      method: 'DELETE',
    });
  }

  getFileUrl(fileId: string) {
    if (this.token) {
      return `${this.collaborationServiceURL}/files/${fileId}/download?token=${this.token}`;
    }
    return `${this.collaborationServiceURL}/files/${fileId}/download`;
  }

  // Talent Service methods for skill management
  async getUserSkills(userId: string) {
    return this.talentServiceRequest(`/skills/${userId}`, {
      method: 'GET'
    });
  }

  async getAvailableSkills() {
    return this.talentServiceRequest('/skills', {
      method: 'GET'
    });
  }

  async getSkills() {
    return this.getAvailableSkills();
  }

  async addUserSkill(userId: string, skillData: any) {
    if (!userId) throw new Error('User ID is required');

    // First, find or create the skill itself
    const skillResponse = await this.talentServiceRequest('/skills', {
      method: 'POST',
      body: JSON.stringify({
        name: skillData.name,
        category: skillData.category,
        description: skillData.description || ''
      })
    });

    // skillResponse is already the JSON body thanks to talentServiceRequest
    const skill = skillResponse?.skill || skillResponse;
    const skillId = skill?._id || skill?.id;

    if (!skillId) {
      console.error('Failed to get skill ID from response:', skillResponse);
      throw new Error('Failed to create or identify skill');
    }

    // Then, link the skill to the talent
    const payload = {
      userId,
      skillId,
      proficiency: skillData.proficiency || 1,
      yearsOfExperience: skillData.yearsOfExperience || 0,
      lastUsed: skillData.lastUsed || new Date().toISOString(),
      endorsements: skillData.endorsements || 0,
      evidence: skillData.evidence || []
    };

    console.log('🔗 Linking talent-skill:', payload);

    return this.talentServiceRequest('/talent-skills', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async updateUserSkill(userId: string, skillId: string, skillData: any) {
    return this.talentServiceRequest(`/skills/${userId}/skill/${skillId}`, {
      method: 'PUT',
      body: JSON.stringify(skillData)
    });
  }

  async deleteUserSkill(userId: string, skillId: string) {
    await this.talentServiceRequest(`/skills/${userId}/skill/${skillId}`, {
      method: 'DELETE'
    });
    return true;
  }

  // ───────────────────────────────────────────────────────
  // Spatial Intelligence / 3D Hub Smart Layer Methods (NEW)
  // ───────────────────────────────────────────────────────

  async getSkillEcosystem() {
    return this.request('/spatial/skill-ecosystem');
  }

  async getOpportunityPredictions() {
    return this.request('/recommendation/opportunity-predictions');
  }

  // Course & Career Methods
  async getCategories() {
    return this.courseServiceRequest('/courses/categories');
  }

  async getCourses(params: any) {
    const queryString = new URLSearchParams(params).toString();
    return this.courseServiceRequest(`/courses?${queryString}`);
  }

  async getDepartments() {
    return this.courseServiceRequest('/departments');
  }

  async getDepartment(id: string) {
    return this.courseServiceRequest(`/departments/${id}`);
  }

  async getDepartmentJobs(id: string) {
    return this.courseServiceRequest(`/departments/${id}/jobs`);
  }

  async getCareerRecommendations(params: any) {
    return this.courseServiceRequest('/careers/recommend', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: { 'Content-Type': 'application/json' }
    });
  }

  async getJobDetails(departmentId: string, jobId: string) {
    return this.courseServiceRequest(`/departments/${departmentId}/jobs/${jobId}`);
  }

  async getJobCourses(departmentId: string, jobId: string, page = 1) {
    return this.courseServiceRequest(`/departments/${departmentId}/jobs/${jobId}/courses?page=${page}`);
  }

  async getDepartmentCourses(departmentId: string, page = 1) {
    return this.courseServiceRequest(`/departments/${departmentId}/courses?page=${page}`);
  }

  async getUserProgress(userId: string) {
    return this.courseServiceRequest(`/progress/user/${userId}`);
  }

  async getUserCourses(userId: string, status?: string) {
    const query = status ? `?status=${status}` : '';
    return this.courseServiceRequest(`/progress/user/${userId}/courses${query}`);
  }

  async updateUserProgress(userId: string, courseId: string, progressData: any) {
    return this.courseServiceRequest(`/progress/user/${userId}/course/${courseId}`, {
      method: 'POST',
      body: JSON.stringify(progressData)
    });
  }

  async getCareerPathProgress(userId: string) {
    return this.courseServiceRequest(`/progress/user/${userId}/jobs`);
  }

  // Resend verification email helper (tries common endpoints)
  async resendVerification(email: string) {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const candidateEndpoints = [
        '/resend-verification',
        '/auth/resend-verification',
        '/users/resend-verification'
      ];

      for (const endpoint of candidateEndpoints) {
        const resp = await fetch(`${this.userServiceURL}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
          signal: controller.signal
        });

        if (resp.ok) {
          return resp.json().catch(() => ({ message: 'Verification email sent' }));
        }

        // If 404, try next known endpoint shape
        if (resp.status === 404) continue;

        const error = await resp.json().catch(() => ({ error: 'Failed to resend verification email' }));
        throw new Error(error.error || 'Failed to resend verification email');
      }

      throw new Error('Verification endpoint not available');
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async getSkillAssessments(userId: string, skillId?: string) {
    try {
      const endpoint = skillId
        ? `/assessments/user/${userId}?skillId=${skillId}`
        : `/assessments/user/${userId}`;

      return await this.talentServiceRequest(endpoint, {
        method: 'GET'
      });
    } catch (error) {
      console.error('Error fetching skill assessments:', error);
      return { assessments: [] }; // Return empty array if assessments can't be loaded
    }
  }

  async submitSkillAssessment(userId: string, skillId: string, assessmentData: any) {
    return this.talentServiceRequest(`/assessments/user/${userId}/skill/${skillId}`, {
      method: 'POST',
      body: JSON.stringify(assessmentData)
    });
  }

  async getServiceTalents(filters: any = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.talentServiceRequest(`/talents?${query}`, {
      method: 'GET'
    });
  }

  // Course Directory Talent Methods (New Hub)
  async getTalents(filters: any = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.courseServiceRequest(`/talents?${query}`, {
      method: 'GET'
    });
  }

  async checkEligibility(userId: string) {
    return this.talentServiceRequest(`/eligibility/${userId}`, {
      method: 'GET'
    });
  }

  // Talent & Skills
  async createTalentProfile(userId: string, profileData: any) {
    return this.talentServiceRequest(`/talents`, {
      method: 'POST',
      body: JSON.stringify({
        userId,
        ...profileData
      }),
    });
  }

  async upsertTalentProfile(data: any) {
    const { userId, ...profileData } = data;
    return this.createTalentProfile(userId, profileData);
  }

  // Talent profile settings (own profile)
  async getTalentSettings() {
    return this.talentServiceRequest('/talent-settings', { method: 'GET' });
  }

  async updateTalentSettings(settings: {
    emailVisible?: boolean;
    openToHire?: boolean;
    openToCollaboration?: boolean;
    showRate?: boolean;
    availabilityStatus?: 'Available' | 'Busy' | 'Offline';
  }) {
    return this.talentServiceRequest('/talent-settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  // --- Network Connections ---
  async sendConnectionRequest(receiverId: string) {
    return this.talentServiceRequest('/connections/request', {
      method: 'POST',
      body: JSON.stringify({ receiverId })
    });
  }

  async respondToConnectionRequest(connectionId: string, status: 'accepted' | 'declined') {
    return this.talentServiceRequest(`/connections/${connectionId}/respond`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  async getConnections() {
    return this.talentServiceRequest('/connections', { method: 'GET' });
  }

  async getPendingConnections() {
    return this.talentServiceRequest('/connections/pending', { method: 'GET' });
  }

  // --- Hiring Requests ---
  async sendHiringRequest(hiringData: any) {
    return this.talentServiceRequest('/hiring/request', {
      method: 'POST',
      body: JSON.stringify(hiringData)
    });
  }

  async respondToHiringRequest(requestId: string, status: 'accepted' | 'declined') {
    return this.talentServiceRequest(`/hiring/${requestId}/respond`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  async getMyHiringRequests() {
    return this.talentServiceRequest('/hiring/requests', { method: 'GET' });
  }

  async uploadEvidence(file: File) {
    const formData = new FormData();
    formData.append('evidence', file);

    const TALENT_SERVICE_URL = import.meta.env.VITE_TALENT_SERVICE_URL || 'https://techunity-ghbh.onrender.com/api/talent';
    const token = this.token || localStorage.getItem('accessToken');

    const response = await fetch(`${TALENT_SERVICE_URL}/evidence/upload`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to upload evidence');
    }

    return response.json();
  }

  // --- Skill Passport & Opportunity Engine ---
  async getSkillPassport(userId: string) {
    return this.talentServiceRequest(`/passport/${userId}`, { method: 'GET' });
  }

  async updateWorkModes(data: {
    workModes?: string[];
    certificationMode?: string;
    informalCredentials?: any;
  }) {
    return this.talentServiceRequest('/work-modes', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async updateGeoLocation(data: {
    lat: number;
    lng: number;
    city?: string;
    country?: string;
    setManually?: boolean;
  }) {
    return this.talentServiceRequest('/geo-location', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async getNearbyTalents(params: {
    lat: number;
    lng: number;
    radiusKm?: number;
    workMode?: string;
  }) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, value.toString());
    });
    return this.talentServiceRequest(`/nearby?${query.toString()}`, { method: 'GET' });
  }

  async downloadSkillPassportPdf(userId: string) {
    const url = `${this.userServiceURL}/passport/${userId}/pdf`;
    const token = this.token || localStorage.getItem('accessToken');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      }
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }

    return response.blob();
  }

  async analyzeSkills(skills: string) {
    return this.talentServiceRequest('/analyze-skills', {
      method: 'POST',
      body: JSON.stringify({ skills })
    });
  }

  async dispatchTask(data: any) {
    return this.collaborationServiceRequest('/dispatch/tasks', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async sendInvite(inviteData: { receiverId: string; roomId?: string; projectId?: string; message?: string }) {
    return this.collaborationServiceRequest('/invites', {
      method: 'POST',
      body: JSON.stringify(inviteData)
    });
  }

  async getInvites() {
    return this.collaborationServiceRequest('/invites');
  }

  async respondToInvite(inviteId: string, status: 'accepted' | 'declined') {
    return this.collaborationServiceRequest(`/invites/${inviteId}/respond`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  async createSmartContract(contractData: any) {
    return this.collaborationServiceRequest('/contracts', {
      method: 'POST',
      body: JSON.stringify(contractData)
    });
  }

  async getMySmartContracts() {
    return this.collaborationServiceRequest('/contracts');
  }

  async updateMilestoneStatus(contractId: string, milestoneId: string, status: 'completed' | 'released') {
    return this.collaborationServiceRequest(`/contracts/${contractId}/milestones/${milestoneId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  // --- Spatial Work Intelligence APIs ---
  async getWorkspaceMemory() {
    return this.request('/spatial/workspace/memory');
  }

  async setEmotionalTheme(theme: string) {
    return this.request('/spatial/workspace/theme', {
      method: 'POST',
      body: JSON.stringify({ theme })
    });
  }

  async evaluatePrototype(modelProperties: any) {
    return this.request('/ai/evaluate-prototype', {
      method: 'POST',
      body: JSON.stringify(modelProperties)
    });
  }

  async getProjectGravityMap() {
    return this.request('/visualization/gravity/map');
  }

  async predictCareerOpportunity() {
    return this.request('/recommendation/predict/career');
  }

  async getEmotionalPresence() {
    return this.request('/presence/emotional-status');
  }
}

// Socket client class
// Socket client class (Stubbed for stability)
// Real Socket Client Implementation
class SocketClient {
  private socket: Socket | null = null;
  private url: string;
  private listeners: Map<string, ((data: any) => void)[]> = new Map();
  private onlineUsers: Set<string> = new Set();
  /** Queue of [event, data] pairs emitted before the socket connected */
  private pendingQueue: Array<[string, any]> = [];

  constructor(url: string) {
    this.url = url;
  }

  connect(token: string) {
    // If already connected, do nothing
    if (this.socket?.connected) {
      return this.socket;
    }

    // If already connecting, don't start another one
    if (this.socket && !this.socket.disconnected) {
      console.log('🔌 Socket already attempting connection...');
      return this.socket;
    }

    console.log('🔌 Connecting to socket:', this.url);

    this.socket = io(this.url, {
      auth: {
        token,                            // for 'auth.token' check
        Authorization: `Bearer ${token}`, // for 'auth.Authorization' check (current backend)
      },
      withCredentials: true,
      transports: ['polling', 'websocket'], // Start with polling for better compatibility with proxies
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,                       // 20s timeout
    } as any);

    this.setupDefaultListeners();

    return this.socket;
  }

  private setupDefaultListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket?.id);

      // Auto-poll for presence on connection
      this.socket?.emit('presence:poll');

      // Flush any queued emits that arrived before connection was ready
      if (this.pendingQueue.length > 0) {
        console.log(`🔄 Flushing ${this.pendingQueue.length} queued socket event(s)`);
        const queued = [...this.pendingQueue];
        this.pendingQueue = [];
        queued.forEach(([event, data]) => {
          this.socket?.emit(event, data);
        });
      }
    });

    this.socket.on('disconnect', (reason: any) => {
      console.log('❌ Socket disconnected:', reason);
      // Clear queued events — they were for the old connection/session context
      this.pendingQueue = [];
      this.onlineUsers.clear();
      this.internalEmit('presence_changed', Array.from(this.onlineUsers));

      if (reason === 'io server disconnect') {
        // the disconnection was initiated by the server, you need to reconnect manually
        this.socket?.connect();
      }
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('⚠️ Socket connection error:', error.message);
    });

    // --- Presence Listeners ---
    this.socket.on('presence:onlineUsers', (userIds: string[]) => {
      this.onlineUsers = new Set(userIds);
      this.internalEmit('presence_changed', Array.from(this.onlineUsers));
    });

    this.socket.on('presence:update', (data: { userId: string; status: string }) => {
      if (data.status === 'online') {
        this.onlineUsers.add(data.userId);
      } else {
        this.onlineUsers.delete(data.userId);
      }
      this.internalEmit('presence_changed', Array.from(this.onlineUsers));
    });

    // --- Notification Listeners ---
    this.socket.on('task_match', (data: any) => {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.success(data.message || 'New task match found!', {
          duration: 6000,
          icon: '🎯',
          style: { background: '#2C1810', color: '#D4AF37', border: '1px solid #D4AF37' }
        });
      });
      this.internalEmit('task_match', data);
    });

    this.socket.on('application_update', (data: any) => {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.success(data.message || 'Application status updated!', {
          icon: '📝',
          style: { background: '#2C1810', color: '#D4AF37', border: '1px solid #D4AF37' }
        });
      });
      this.internalEmit('application_update', data);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      console.log('🔌 Socket manually disconnected');
    }
  }

  getSocket() {
    return this.socket;
  }

  // Generic Event Listener
  on(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.on(event, callback);
    } else {
      console.warn(`Cannot listen to '${event}': Socket not initialized`);
    }
  }

  off(event: string, callback?: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  // Helper to emit events safely — queues if not yet connected
  private emit(event: string, data: any) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
    } else if (this.socket) {
      // Socket exists but not connected yet — queue for flush on connect
      this.pendingQueue.push([event, data]);
      console.debug(`🕐 Queued socket event until connected: ${event}`);
    } else {
      console.debug(`Socket not initialised. Dropping event: ${event}`);
    }
  }

  // --- Room Management ---
  joinRoom(roomId: string) {
    this.emit('room:join', { roomId });
  }

  leaveRoom(roomId: string) {
    this.emit('room:leave', { roomId });
  }

  updateRoomSettings(roomId: string, settings: any) {
    this.emit('room:updateSettings', { roomId, settings });
  }

  // --- Chat Features ---
  sendMessage(roomId: string, content: string, type = 'text', metadata?: any) {
    this.emit('chat:sendMessage', { roomId, content, type, metadata });
  }

  getChatHistory(roomId: string, limit = 50, offset = 0) {
    this.emit('chat:getHistory', { roomId, limit, offset });
  }

  setTyping(roomId: string, isTyping: boolean) {
    this.emit('chat:typing', { roomId, isTyping });
  }

  addReaction(messageId: string, emoji: string) {
    this.emit('chat:addReaction', { messageId, emoji });
  }

  removeReaction(messageId: string) {
    this.emit('chat:removeReaction', { messageId });
  }

  // --- Video / WebRTC Events ---
  joinVideoCall(roomId: string, mediaState: any) {
    this.emit('video:join', { roomId, mediaState });
  }

  leaveVideoCall(roomId: string) {
    this.emit('video:leave', { roomId });
  }

  updateMediaState(roomId: string, mediaState: any) {
    this.emit('video:updateMedia', { roomId, mediaState });
  }

  // Signaling for WebRTC
  sendOffer(roomId: string, targetUserId: string, offer: any) {
    this.emit('video:offer', { roomId, targetUserId, offer });
  }

  sendAnswer(roomId: string, targetUserId: string, answer: any) {
    this.emit('video:answer', { roomId, targetUserId, answer });
  }

  sendIceCandidate(roomId: string, targetUserId: string, candidate: any) {
    this.emit('video:iceCandidate', { roomId, targetUserId, candidate });
  }

  startScreenShare(roomId: string) {
    this.emit('video:startScreenShare', { roomId });
  }

  stopScreenShare(roomId: string) {
    this.emit('stop_screen_share', { roomId });
  }

  // --- Whiteboard Events ---
  joinWhiteboard(roomId: string) {
    this.emit('join_whiteboard', { roomId });
  }

  leaveWhiteboard(roomId: string) {
    this.emit('leave_whiteboard', { roomId });
  }

  startDrawing(roomId: string, point: any, tool: any) {
    this.emit('wb_start_drawing', { roomId, point, tool });
  }

  drawing(roomId: string, point: any) {
    this.emit('wb_drawing', { roomId, point });
  }

  stopDrawing(roomId: string, stroke: any) {
    this.emit('wb_stop_drawing', { roomId, stroke });
  }

  eraseWhiteboard(roomId: string, area: any) {
    this.emit('wb_erase', { roomId, area });
  }

  clearWhiteboard(roomId: string) {
    this.emit('wb_clear', { roomId });
  }

  saveWhiteboard(roomId: string, name?: string) {
    this.emit('wb_save', { roomId, name });
  }

  loadWhiteboard(roomId: string, whiteboardId: string) {
    this.emit('wb_load', { roomId, whiteboardId });
  }

  // --- 3D Hub & Presence ---
  updateAvatar(roomId: string, avatar: any) {
    this.emit('update_avatar', { roomId, avatar });
  }

  updateObject(roomId: string, object: any) {
    this.emit('update_object', { roomId, object });
  }

  updateWorld(roomId: string, world: any) {
    this.emit('update_world', { roomId, world });
  }

  updatePresence(status: 'online' | 'away' | 'busy') {
    this.emit('presence_update', { status });
  }

  // --- Internal Listener System (for React components) ---
  private internalEmit(event: string, data: any) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(cb => cb(data));
    }
  }

  subscribe(event: string, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback);

    return () => {
      const list = this.listeners.get(event);
      if (list) {
        this.listeners.set(event, list.filter(cb => cb !== callback));
      }
    };
  }

  refreshPresence() {
    if (this.socket?.connected) {
      this.socket.emit('presence:poll');
    }
  }

  getOnlineUsers(): string[] {
    return Array.from(this.onlineUsers);
  }
}

// Export the singleton instance
export const apiClient = new ApiClient(
  FINAL_BASE_URL,
  FINAL_USER_URL,
  FINAL_COLLAB_URL,
  FINAL_TALENT_URL,
  FINAL_FINANCIAL_URL,
  FINAL_COURSE_URL,
  FINAL_TIMESERIES_URL,
  FINAL_SNN_URL
);
export const socketClient = new SocketClient(SOCKET_URL);

// Re-export types from types/index.ts
export * from '../types';
